'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

import { useFavorites } from '@/features/favorites';
import { Topic } from '@/components/Topic';
import { getV1ArticlesId as getArticlesId } from '@/shared/api/__generated__/endpoints/articles/articles';

const PAGE_PER_REQUEST = 10;

export function FavoritesView() {
  const { favorites, setHydrated, hydrated } = useFavorites(
    useShallow((state) => ({
      favorites: state.articles,
      hydrated: state.hydrated,
      setHydrated: state.setHydrated,
    }))
  );

  const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['favorites'],
    queryFn: async ({ pageParam }) => {
      const response = await Promise.all(
        favorites
          .slice(pageParam, pageParam + PAGE_PER_REQUEST)
          .map((item) => getArticlesId(item))
      );

      setHydrated();
      return {
        lastCursor: pageParam,
        data: response
          .map((item) => (item.status === 200 ? item.data : null))
          .filter((item) => !!item),
      };
    },
    getNextPageParam: (last) => {
      const next = last.lastCursor + PAGE_PER_REQUEST;
      return next < favorites.length ? next : undefined;
    },
    initialPageParam: 0,
    enabled: favorites.length > 0,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const articles = useMemo(() => {
    return (
      data?.pages
        .flatMap(({ data }) => data)
        .filter((data) => favorites.includes(data.id ?? '')) ?? []
    );
  }, [data, favorites]);

  return (
    <Topic
      title="Избранное"
      articles={articles}
      isFetching={isFetching || !hydrated}
      hasNextPage={hasNextPage}
      onFetchMore={fetchNextPage}
    />
  );
}
